import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Person } from '../../../../../services/person'
import { User } from '../../../../../services/users'
import { APIKey } from '../../../../../services/apiKey'

import { DefaultLoader } from '../../../../../components/DefaultLoader'
import { pages } from '../../../../../config/pages'

import { PersonFormatted, formatPerson } from '../../../utils/formatPerson'

import { useFetchPerson, useFetchPartner, useFetchApiKeys } from './hooks'
import { useCan } from '../../../../../hooks/useCan'
import { can } from '../../../../../config/permissions'

interface PersonFormContextContent {
  person?: PersonFormatted
  responsible?: Person
  partner?: User
  apiKeys: APIKey[]
  loading: boolean
  setLoading: (value: boolean) => void
  getPersonState: () => PersonFormatted
  updatePersonState: (person: Person) => PersonFormatted
  setApiKeys: (value: APIKey[]) => void
}

export const PersonFormContext = React.createContext<PersonFormContextContent>(
  {} as PersonFormContextContent
)

interface PersonFormProviderProps {
  children: React.ReactNode
}

export const PersonFormProvider: React.FC<PersonFormProviderProps> = ({
  children
}) => {
  const [person, setPerson] = useState<PersonFormatted>()
  const [responsible, setResponsible] = useState<Person>()
  const [partner, setPartner] = useState<User>()
  const [apiKeys, setApiKeys] = useState<APIKey[]>([])
  const [loading, setLoading] = useState(false)

  const [query] = useSearchParams()
  const canReadApiKeys = useCan(can.person.readApiKeys)

  const fetchPerson = useFetchPerson()
  const fetchPartner = useFetchPartner()
  const fetchApiKeys = useFetchApiKeys()

  const navigate = useNavigate()

  function getPersonState(): PersonFormatted {
    if (!person) throw new Error('Nenhum cadastro foi selecionado')
    return person
  }

  function updatePersonState(personData: Person): PersonFormatted {
    const formatted = formatPerson(personData)
    setPerson(formatted)
    return formatted
  }

  useEffect(() => {
    const personId = query.get('id')
    if (!personId) return

    setLoading(true)

    fetchPerson(personId)
      .then(personData => {
        if (!personData) {
          navigate(`${pages.people.path}/inclusao`)
          toast.warn(
            'O cadastro não foi encontrado, mas você pode criar um novo.'
          )
          return Promise.reject(new Error('Person not found'))
        }

        const partnerPromise = personData.partnerId
          ? fetchPartner(personData.partnerId)
          : Promise.resolve(undefined)

        const responsiblePromise = personData.responsibleId
          ? fetchPerson(personData.responsibleId)
          : Promise.resolve(undefined)

        const apiKeysPromise = canReadApiKeys
          ? fetchApiKeys(personData.id)
          : Promise.resolve([])

        return Promise.all([
          partnerPromise,
          responsiblePromise,
          apiKeysPromise
        ]).then(([partnerData, responsibleData, apiKeysData]) => {
          setPartner(partnerData)
          setResponsible(responsibleData)
          setApiKeys(apiKeysData)

          updatePersonState(personData)
        })
      })
      .catch(err => {
        if (err.message !== 'Person not found') {
          console.error(err)
          toast.error('Ocorreu um erro ao carregar os dados da pessoa.')
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  return (
    <PersonFormContext.Provider
      value={{
        person,
        responsible,
        partner,
        apiKeys,
        loading,
        setLoading,
        getPersonState,
        updatePersonState,
        setApiKeys
      }}
    >
      <DefaultLoader active={loading} />
      {children}
    </PersonFormContext.Provider>
  )
}
